import { isSupportedLocale, type PortalLocale } from "@/lib/i18n/portal/config";
import { isIosCompanionApp } from "@/lib/native/runtime";

export type MetaLinkProvider = "FACEBOOK" | "INSTAGRAM";

export function parseMetaLinkProvider(
  params: URLSearchParams,
): MetaLinkProvider | null {
  if (params.getAll("provider").length !== 1) return null;
  const provider = params.get("provider");
  return provider === "FACEBOOK" || provider === "INSTAGRAM" ? provider : null;
}

export function metaLinkPath(
  locale: PortalLocale,
  provider: MetaLinkProvider,
): string {
  return `/${locale}/meta/link?provider=${provider}`;
}

export function metaLinkProviderFromReturnPath(
  value: string | null,
  locale: PortalLocale,
): MetaLinkProvider | null {
  return (
    (["FACEBOOK", "INSTAGRAM"] as const).find(
      (provider) => value === metaLinkPath(locale, provider),
    ) ?? null
  );
}

/** Retain local invite/auth returns without accepting browser URL escapes. */
export function safeAuthReturnPath(value: string | null): string | null {
  if (!value || value.length > 2048) return null;
  let decoded = value;
  try {
    for (let depth = 0; depth < 4; depth += 1) {
      if (
        !decoded.startsWith("/") ||
        decoded.startsWith("//") ||
        Array.from(decoded).some((character) => {
          const code = character.charCodeAt(0);
          return (
            character === "\\" || code <= 32 || (code >= 127 && code <= 159)
          );
        })
      )
        return null;
      const path = decoded.split(/[?#]/, 1)[0];
      if (
        !isSupportedLocale(path.split("/")[1] ?? "") ||
        path.split("/").some((part) => part === "." || part === "..")
      )
        return null;
      const next = decodeURIComponent(decoded);
      if (next === decoded) {
        // Returning to these entry pages would lose the intent or create a loop.
        if (
          /^\/(?:ar|en)\/(?:login|change-password|meta\/callback)\/?$/.test(
            path,
          ) || (isIosCompanionApp() && /^\/(?:ar|en)\/meta\/link\/?$/.test(path))
        )
          return null;
        return value;
      }
      decoded = next;
    }
  } catch {
    // Malformed escapes must not be interpreted differently by the router.
  }
  return null;
}

export function withAuthReturnPath(path: string, next: string | null): string {
  const safeNext = safeAuthReturnPath(next);
  return safeNext
    ? `${path}${path.includes("?") ? "&" : "?"}next=${encodeURIComponent(safeNext)}`
    : path;
}
